import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { ICognitoMigrationPatient } from "../../../@types/admin/AdminCognitoMigration";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { cognitoMigrationService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export const COGNITO_MIGRATION_PATIENTS_QUERY_KEY =
  "admin-cognito-migration-patients";

/**
 * A page of one hospital's active patients with their Cognito status (EN-5222).
 * Disabled until a hospital is chosen: the list is scoped to one, and every
 * row costs the API a Cognito lookup.
 */
export function useCognitoMigrationPatients(
  hospitalGroupId: number | undefined,
  wardId: number | undefined,
  search: string,
  page: number,
  pageSize: number
) {
  const { data, isLoading, isFetching, isError } = useQuery<
    IPaginatedResponse<ICognitoMigrationPatient[]>
  >({
    queryKey: [
      COGNITO_MIGRATION_PATIENTS_QUERY_KEY,
      hospitalGroupId,
      wardId ?? null,
      search,
      page,
      pageSize,
    ],
    queryFn: async () =>
      ensureOk(
        await cognitoMigrationService.getPatients({
          hospitalGroupId: hospitalGroupId!,
          wardId,
          search,
          page,
          pageSize,
        })
      ),
    enabled: hospitalGroupId !== undefined,
    placeholderData: keepPreviousData,
    // Statuses change under the page (a run just migrated some of them), so
    // re-opening it must re-read rather than show a 3-minute-old cache.
    refetchOnMount: "always",
  });

  return {
    patients: data?.result ?? [],
    pagination: {
      currentPage: data?.currentPage ?? page,
      pageSize: data?.pageSize ?? pageSize,
      totalPages: data?.pages ?? 0,
      totalItems: data?.totalCount ?? 0,
      hasPreviousPage: data?.hasPreviousPage ?? false,
      hasNextPage: data?.hasNextPage ?? false,
    },
    isLoading,
    isFetching,
    isError,
  };
}
